import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { AdminLayout, RequireAdmin, RequireFullAdmin } from './admin/AdminLayout.js';
import { AdminChannelsPage } from './admin/pages/AdminChannelsPage.js';
import { AdminDirectoryPage } from './admin/pages/AdminDirectoryPage.js';
import { AdminLoginPage } from './admin/pages/AdminLoginPage.js';
import { AdminOverviewPage } from './admin/pages/AdminOverviewPage.js';
import { AdminSettingsPage } from './admin/pages/AdminSettingsPage.js';
import { AdminUsersPage } from './admin/pages/AdminUsersPage.js';
import { useAuthStore } from './auth/authStore.js';
import { RedirectIfAuthenticated } from './auth/RedirectIfAuthenticated.js';
import { RequireAuth } from './auth/RequireAuth.js';
import { AppLayout } from './components/AppLayout.js';
import { ChatPage } from './pages/ChatPage.js';
import { LoginPage } from './pages/LoginPage.js';
import { NotFoundPage } from './pages/NotFoundPage.js';
import { RegisterPage } from './pages/RegisterPage.js';
import { useSettingsStore } from './settings/settingsStore.js';
import { SocketProvider } from './socket/SocketProvider.js';

export function App() {
  const bootstrap = useAuthStore((state) => state.bootstrap);
  const loadSettings = useSettingsStore((state) => state.load);

  // Validates any stored token before the first render decision is made, so a
  // refresh does not flash the login screen at an authenticated user.
  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  // Public, so the sign-up form has the password policy before any account exists.
  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  return (
    <SocketProvider>
      <Routes>
        <Route element={<RedirectIfAuthenticated />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
        </Route>

        <Route element={<RequireAuth />}>
          <Route element={<AppLayout />}>
            <Route path="/" element={<ChatPage />} />
            <Route path="/channels/:channelId" element={<ChatPage />} />
          </Route>
        </Route>

        {/* The administrator panel: its own sign-in, its own session, no socket. */}
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin" element={<RequireAdmin />}>
          <Route element={<AdminLayout />}>
            <Route index element={<AdminOverviewPage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route element={<RequireFullAdmin />}>
              <Route path="channels" element={<AdminChannelsPage />} />
              <Route path="directory" element={<AdminDirectoryPage />} />
              <Route path="settings" element={<AdminSettingsPage />} />
            </Route>
          </Route>
        </Route>

        <Route path="/404" element={<NotFoundPage />} />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </SocketProvider>
  );
}
